import type { Link as ReactRouterLink } from 'react-router-dom';
import { merge } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { HeightTypes } from '../../utilities/shared/Height';
import type { MaxHeightTypes } from '../../utilities/shared/MaxHeight';
import type { MinHeightTypes } from '../../utilities/shared/MinHeight';
import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import type { LinkProps } from '../Link/Link';
import { outlineColors, outlines } from '../../common/outlines';
import { selectors } from '../../common/selectors';
import { transitions } from '../../common/transitions';
import { Button } from '../../controls/Button/Button';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { colors, darkThemeSelector, keyframes, shadows, styled } from '../../stitches.config';
import { heightCSS } from '../../utilities/shared/Height';
import { maxHeightCSS } from '../../utilities/shared/MaxHeight';
import { minHeightCSS } from '../../utilities/shared/MinHeight';

const CAROUSEL_PRESSABLE_TRANSITION_DURATION = 350;
const CAROUSEL_AUTO_ADVANCE_INTERVAL = 10000;
const CAROUSEL_AUTO_ADVANCE_INDICATOR_DRAIN_DURATION = 350;

const CarouselContent = styled('div', {
  maxWidth: '100%',
  maxHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
  borderRadius: '$12',
  overflow: 'hidden',
});
CarouselContent.displayName = 'CarouselContent';

const CarouselPressable = styled('div', {
  flexShrink: 0,
  flexBasis: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  widthAll: '100%',
  heightAll: '100%',
  transitionProperty: 'transform',
  transitionDuration: `${CAROUSEL_PRESSABLE_TRANSITION_DURATION}ms`,
  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  transform: `translateX(calc(var(--relative-index) * 100%))`,

  '&::before': {
    content: '',
    position: 'absolute',
    inset: '$6',
    borderRadius: '$8',
    transition: transitions.focusRing,
  },

  [selectors.focus]: {
    outline: 'none',

    '&::before': {
      boxShadow: shadows.focusRingLight,

      [darkThemeSelector]: {
        boxShadow: shadows.focusRingDark,
      },
    },
  },
});
CarouselPressable.displayName = 'CarouselPressable';

const CarouselDisplay = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  heightAll: '100%',
  overflow: 'hidden',
});
CarouselDisplay.displayName = 'CarouselDisplay';

const CarouselNavigator = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  alignItems: 'center',
  widthAll: '100%',
  heightAll: '100%',
});
CarouselNavigator.displayName = 'CarouselNavigator';

const carouselIndicatorProgressFill = keyframes({
  from: { transform: 'translateX(-100%)' },
  to: { transform: 'translateX(0)' },
});

const carouselIndicatorProgressDrain = keyframes({
  from: { transform: 'translateX(0)' },
  to: { transform: 'translateX(100%)' },
});

const CarouselIndicatorProgress = styled('div', {
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: '100%',
  background: colors.chartFillBrandLight,
  borderRadius: '99em',
  animationTimingFunction: 'linear',
  animationFillMode: 'forwards',

  [darkThemeSelector]: {
    background: colors.chartFillBrandDark,
  },
});
CarouselIndicatorProgress.displayName = 'CarouselIndicatorProgress';

const CarouselIndicator = styled('button', {
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  widthAll: '$12',
  heightAll: '$4',
  background: colors.gray200,
  borderRadius: '99em',
  cursor: 'pointer',
  transition: transitions.focusRing,

  [darkThemeSelector]: {
    background: colors.gray700,
  },

  [selectors.focus]: {
    boxShadow: shadows.focusRingLight,
    outline: 'none',

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  variants: {
    isSelected: {
      true: {
        background: colors.chartFillBrandLight,

        [darkThemeSelector]: {
          background: colors.chartFillBrandDark,
        },
      },
      false: {},
    },
    showProgress: {
      true: {},
      false: {},
    },
    isDraining: {
      true: {
        background: colors.gray200,

        [darkThemeSelector]: {
          background: colors.gray700,
        },
      },
      false: {},
    },
  },
  compoundVariants: [
    {
      isSelected: true,
      showProgress: true,
      css: {
        background: colors.brand200,

        [darkThemeSelector]: {
          background: colors.brand700,
        },
      },
    },
  ],
});
CarouselIndicator.displayName = 'CarouselIndicator';

const CarouselIndicators = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$4',
  heightAll: '100%',
  padding: '0 $12',
  outline: outlines.control,
  outlineColor: outlineColors.control.secondaryMutedInitialLight,

  [darkThemeSelector]: {
    outlineColor: outlineColors.control.secondaryMutedInitialDark,
  },
});
CarouselIndicators.displayName = 'CarouselIndicators';

const CarouselControls = styled(ControlGroup, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  widthAll: '100%',
});
CarouselControls.displayName = 'CarouselControls';

const CarouselContainer = styled('div', {
  display: 'grid',
  gridTemplateRows: '1fr min-content',
  gridTemplateColumns: '1fr',
  gridTemplateAreas: '"navigator" "indicators"',
  gap: '$12',
});
CarouselContainer.displayName = 'CarouselContainer';

type CarouselItemLink = 'a' | 'div' | typeof ReactRouterLink;

export interface CarouselItemProps<T extends CarouselItemLink = 'a'> {
  /**
   * A unique identifier for the carousel item (required for valid keys).
   */
  id: string;
  /**
   * The source of the image to display in the carousel.
   */
  imageSrc: string;
  /**
   * Alternative text for the image. The image is the whole of the slide, so when
   * `linkProps` makes that slide a link this is also the link's only name —
   * describe the destination.
   *
   * Required, but `''` is allowed and marks the image decorative, hiding it from
   * screen readers. Only pass `''` for a slide that does not link anywhere: a
   * link whose sole content is a decorative image has no name at all.
   */
  imageAlt: string;
  /**
   * The props to spread onto the link component.
   */
  linkProps: Omit<PolymorphicComponentProps<T, LinkProps>, 'ref'>;
}
export interface CarouselProps<Tag extends CarouselItemLink = 'a'> {
  /**
   * Set a height (can use the sub-props `height`, `maxHeight`, and `minHeight` inside of `height` too).
   */
  height?:
    | HeightTypes
    | {
        height?: HeightTypes;
        maxHeight?: MaxHeightTypes;
        minHeight?: MinHeightTypes;
      };
  /**
   * When set, the carousel automatically advances to the next slide.
   * The timer resets whenever the user manually changes the slide (prev/next/indicator).
   * @default interval 10000ms when autoAdvance is enabled but interval is omitted
   */
  autoAdvance?: {
    /**
     * Milliseconds between automatic advances.
     */
    interval?: number;
  };
  /**
   * The items to display in the carousel as an array of `CarouselItemProps`.
   */
  items: CarouselItemProps<Tag>[];
}

/**
 * Slides default to `a` (matching the default type parameter of `CarouselItemProps`),
 * so anything the caller has not explicitly opted out of with `as="div"` renders as a
 * real link and belongs in the tab order.
 */
function isSlideALink(linkAs: unknown) {
  return linkAs !== 'div';
}

function goToNext(current: number, total: number) {
  return (current + 1) % total;
}

function goToPrev(current: number, total: number) {
  return current === 0 ? total - 1 : current - 1;
}

export function Carousel<Tag extends CarouselItemLink = 'a'>({
  autoAdvance,
  height,
  items,
}: CarouselProps<Tag>) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [drainingIndex, setDrainingIndex] = useState<number | null>(null);
  const [isPausedFromFocus, setIsPausedFromFocus] = useState(false);
  const slideContentRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevIndexRef = useRef(0);

  const autoAdvanceIntervalMs =
    autoAdvance != null ? autoAdvance.interval ?? CAROUSEL_AUTO_ADVANCE_INTERVAL : undefined;

  const advanceTo = useCallback(
    (nextIndex: number) => {
      const prev = prevIndexRef.current;
      prevIndexRef.current = nextIndex;
      setCurrentIndex(nextIndex);
      if (prev !== nextIndex && autoAdvanceIntervalMs != null && autoAdvanceIntervalMs > 0) {
        setDrainingIndex(prev);
        setTimeout(() => setDrainingIndex(null), CAROUSEL_AUTO_ADVANCE_INDICATOR_DRAIN_DURATION);
      }
    },
    [autoAdvanceIntervalMs],
  );

  useEffect(() => {
    prevIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    if (
      autoAdvanceIntervalMs == null ||
      autoAdvanceIntervalMs <= 0 ||
      items.length <= 1 ||
      isPausedFromFocus
    ) {
      return undefined;
    }

    const id = setInterval(() => {
      advanceTo(goToNext(prevIndexRef.current, items.length));
    }, autoAdvanceIntervalMs);

    return () => clearInterval(id);
  }, [currentIndex, autoAdvanceIntervalMs, items.length, advanceTo, isPausedFromFocus]);

  useEffect(() => {
    const element = slideContentRef.current;
    if (element == null) return () => {};

    const handleFocusIn = () => {
      setIsPausedFromFocus(true);
    };
    const handleFocusOut = (event: FocusEvent) => {
      const { relatedTarget } = event;
      if (relatedTarget == null || !element.contains(relatedTarget as Node)) {
        setIsPausedFromFocus(false);
      }
    };

    element.addEventListener('focusin', handleFocusIn);
    element.addEventListener('focusout', handleFocusOut);
    return () => {
      element.removeEventListener('focusin', handleFocusIn);
      element.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    const slideContent = slideContentRef.current;
    if (element == null || slideContent == null || items.length <= 1) return () => {};

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        const nextIndex = goToPrev(prevIndexRef.current, items.length);
        advanceTo(nextIndex);
        const nextItem = items[nextIndex];
        if (slideContent.contains(event.target as Node) && isSlideALink(nextItem?.linkProps?.as)) {
          slideRefs.current[nextIndex]?.focus();
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        const nextIndex = goToNext(prevIndexRef.current, items.length);
        advanceTo(nextIndex);
        const nextItem = items[nextIndex];
        if (slideContent.contains(event.target as Node) && isSlideALink(nextItem?.linkProps?.as)) {
          slideRefs.current[nextIndex]?.focus();
        }
      }
    };

    element.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => element.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [items, advanceTo]);

  if (!items.length) {
    return null;
  }

  type HeightObject = {
    height?: HeightTypes;
    maxHeight?: MaxHeightTypes;
    minHeight?: MinHeightTypes;
  };
  const heightValue =
    typeof height === 'object' && height !== null ? (height as HeightObject).height : height;

  const css = merge(
    heightCSS(heightValue),
    maxHeightCSS(
      typeof height === 'object' && height !== null ? (height as HeightObject).maxHeight : height,
    ),
    minHeightCSS(
      typeof height === 'object' && height !== null ? (height as HeightObject).minHeight : height,
    ),
  );

  return (
    <CarouselContainer ref={containerRef} css={css}>
      <CarouselNavigator>
        <CarouselDisplay
          ref={slideContentRef}
          css={{
            '--relative-index': 0 - currentIndex,
          }}
        >
          {items.map((item, index) => {
            const { as: linkAs = 'a', ...restLinkProps } = item.linkProps;
            const isCurrentSlide = index === currentIndex;
            const isLink = isSlideALink(linkAs);
            return (
              <CarouselPressable
                ref={(el: HTMLElement | null) => {
                  slideRefs.current[index] = el;
                }}
                key={item.id}
                as={linkAs}
                tabIndex={isLink && isCurrentSlide ? 0 : -1}
                {...(restLinkProps as React.ComponentPropsWithoutRef<typeof CarouselPressable>)}
              >
                <CarouselContent as="img" alt={item.imageAlt} src={item.imageSrc} />
              </CarouselPressable>
            );
          })}
        </CarouselDisplay>
      </CarouselNavigator>
      <CarouselControls>
        <Button
          type="button"
          onClick={() => advanceTo(goToPrev(prevIndexRef.current, items.length))}
          appearance="muted"
          arrangement="hidden-label"
          icon="chevron-left"
          variant="secondary"
        >
          Previous
        </Button>
        <CarouselIndicators>
          {items.map((item, index) => {
            const isSelected = index === currentIndex;
            const isDraining = index === drainingIndex;
            const drainDirection = currentIndex > index ? 'normal' : 'reverse';
            const drainAnimation =
              drainDirection === 'normal'
                ? carouselIndicatorProgressDrain
                : carouselIndicatorProgressFill;
            const showProgress =
              isSelected && autoAdvanceIntervalMs != null && autoAdvanceIntervalMs > 0;
            return (
              <CarouselIndicator
                key={item.id}
                type="button"
                tabIndex={isSelected ? -1 : 0}
                aria-label={isSelected ? 'Current slide' : `Go to slide ${index + 1}`}
                onClick={() => advanceTo(index)}
                isSelected={isSelected}
                showProgress={showProgress && !isDraining}
                isDraining={isDraining}
              >
                {showProgress && (
                  <CarouselIndicatorProgress
                    css={{
                      animation: `${carouselIndicatorProgressFill} ${autoAdvanceIntervalMs}ms linear forwards`,
                    }}
                  />
                )}
                {isDraining && (
                  <CarouselIndicatorProgress
                    css={{
                      animation: `${drainAnimation} ${CAROUSEL_AUTO_ADVANCE_INDICATOR_DRAIN_DURATION}ms ease-out ${drainDirection} forwards`,
                    }}
                  />
                )}
              </CarouselIndicator>
            );
          })}
        </CarouselIndicators>
        <Button
          type="button"
          onClick={() => advanceTo(goToNext(prevIndexRef.current, items.length))}
          appearance="muted"
          arrangement="hidden-label"
          icon="chevron-right"
          variant="secondary"
        >
          Next
        </Button>
      </CarouselControls>
    </CarouselContainer>
  );
}
